/* eslint-disable import/no-extraneous-dependencies */
import { ApolloClient, ApolloLink, from, InMemoryCache, split } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { createHttpLink } from '@apollo/client/link/http';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient, Client as GraphQLWsClient } from 'graphql-ws';
import { v4 } from 'uuid';
import { OperationDefinitionNode } from 'graphql';
import { createUploadLink } from 'apollo-upload-client';
import { loadErrorMessages, loadDevMessages } from '@apollo/client/dev';

import { StorageUtils, Logger } from '../utils';
import environment from '../environment';

// Load Apollo Client dev messages in development
if (process.env.NODE_ENV === 'development') {
  // Adds messages only in a dev environment
  loadDevMessages();
  loadErrorMessages();
}

// Use ws:// for localhost development, wss:// for production
const protocol = environment.SUB_URL.includes('localhost') ? 'ws' : 'wss';
const uri = `${protocol}://${environment.SUB_URL}/graphql`;
const apiUri = `${environment.API_URL}/graphql`;

let id: string;
const getNewId = () => {
  id = v4();
  return id;
};

const shouldUseWebSocket = typeof window !== 'undefined';

// Function to create Apollo client with Keycloak context
export const createApolloClient = (getKeycloakToken: () => string | undefined) => {
  const createWsClient = (): GraphQLWsClient | null => {
    if (!shouldUseWebSocket) {
      return null;
    }

    return createClient({
      url: uri,
      lazy: true,
      keepAlive: 30000,
      retryAttempts: Infinity,
      shouldRetry: () => {
        return true;
      },
      connectionParams: () => {
        const token = getKeycloakToken();
        //console.log('token', token);
        //console.log('clientId', !id ? getNewId() : id);
        //console.log('selectedCustomer', StorageUtils.getCustomerId());

        return {
          authToken: token ? `Bearer ${token}` : undefined,
          clientId: !id ? getNewId() : id,
          selectedCustomer: StorageUtils.getCustomerId(),
        };
      },
    });
  };

  const subscriptionClient = createWsClient();
  const wsLink = subscriptionClient ? new GraphQLWsLink(subscriptionClient) : null;

  const authLink = setContext((context, { headers }) => {
    const newHeaders = { ...headers };
    const token = getKeycloakToken();

    if (token) {
      newHeaders.Authorization = `Bearer ${token}`;
      newHeaders.selectedCustomer = StorageUtils.getCustomerId();
    }

    return { headers: newHeaders };
  });

  const httpLink = from([authLink, createHttpLink({ uri: apiUri })]);

  const httpOrWsLink = wsLink
    ? split(
        ({ query }) => {
          const { kind, operation } = getMainDefinition(query) as OperationDefinitionNode;
          return kind === 'OperationDefinition' && operation === 'subscription';
        },
        wsLink,
        httpLink,
      )
    : httpLink;

  const uploadLink = ApolloLink.from([
    authLink,
    createUploadLink({ uri: apiUri, includeExtensions: true }),
  ]);

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.map(({ message, locations, path }) => {
        return Logger.error(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
        );
      });
    }

    if (networkError) {
      Logger.error(`[Network error]: ${networkError}`);
    }
  });

  const link = ApolloLink.split(
    (operation) => {
      return operation.getContext().hasUpload;
    },
    uploadLink,
    httpOrWsLink,
  );

  return new ApolloClient({
    link: from([errorLink, link]),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'no-cache',
        nextFetchPolicy: 'no-cache',
        errorPolicy: 'all',
      },
      query: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      },
    },
  });
};

// Legacy export for backward compatibility - will be removed
export const subscriptionClient = null;
export const client = null;
